import React, { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import UniversalTextField from "../../../../GlobalComponents/UniversalTextField";

import { useAtom } from "jotai";
import { themeAtom, isMobileAtom, isAdminAtom } from "../../../../-Atoms";
import { db, collection, query, orderBy, where, limit, getDocs, getDoc, doc, setDoc, updateDoc } from "../../../../firebaseConfig";

const JobNumber = ({ jobData, allMultipleDayJobs = [], setHasUnsavedChanges }) => {
  const [theme] = useAtom(themeAtom);
  const [isMobile] = useAtom(isMobileAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [jobNumber, setJobNumber] = useState(jobData.jobNumber);
  const [assignJobNumberText, setAssignJobNumberText] =
    useState("Assign Job Number");
  const [isLoading, setIsLoading] = useState(false);
  const [assignJobNumberDisabled, setAssignJobNumberDisabled] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateJobs, setDuplicateJobs] = useState([]);
  const [pendingJobNumber, setPendingJobNumber] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    setJobNumber(jobData.jobNumber);
  }, [jobData]);

  useEffect(() => {
    const { jobTypeFull, dayType, creativeDayType } = jobData;

    if (
      jobTypeFull?.includes("-L") ||
      jobTypeFull?.includes("-LC") ||
      jobTypeFull?.includes("-PC")
    ) {
      if (dayType || creativeDayType) {
        setAssignJobNumberText("Assign Job Number");
        if (jobNumber) {
          setAssignJobNumberDisabled(true);
        } else {
          setAssignJobNumberDisabled(false);
        }
      } else {
        setAssignJobNumberText("Set Day Type");
        setAssignJobNumberDisabled(true);
      }
    } else {
      if (jobNumber) {
        setAssignJobNumberDisabled(true);
      } else {
        setAssignJobNumberDisabled(false);
      }
    }
  }, [
    jobNumber,
    jobData.dayType,
    jobData.creativeDayType,
    jobData.jobTypeFull,
  ]);

  const updateJobName = useCallback(
    (jobNumberChange, clientChange, multipleDaysChange, jobTypeFullChange) => {
      try {
        const jtsRaw = jobTypeFullChange?.match(/^.*\((-.*)\)/);
        let jts = "";
        if (jtsRaw) {
          jts = jtsRaw[1];
        }

        const jn = `${jobNumberChange}-${clientChange}${
          multipleDaysChange ? `-${multipleDaysChange}` : ""
        }${jts}`;

        setDoc(doc(db, "JobData", jobData.id), { jobName: jn || null }, { merge: true });
      } catch (e) {
        console.error("Error updating job name:", e);
      }
    },
    [jobData]
  );

  // Sync job number across all days in a multi-day job
  const updateMultiDayJobNumbers = useCallback(async (newJobNumber) => {
    if (!jobData.isMultiDay && !jobData.parentJobId) return;
    if (allMultipleDayJobs.length <= 1) return;

    try {
      const relatedJobs = allMultipleDayJobs.filter(job => job.id !== jobData.id);

      await Promise.all(relatedJobs.map(async (job) => {
        await updateDoc(doc(db, "JobData", job.id), {
          jobNumber: parseInt(newJobNumber)
        });

        const jtsRaw = job.jobTypeFull?.match(/^.*\((-.*)\)/);
        const jts = jtsRaw ? jtsRaw[1] : "";
        const newJobName = `${newJobNumber}-${job.client}${
          job.multipleDays ? `-${job.multipleDays}` : ""
        }${jts}`;

        await updateDoc(doc(db, "JobData", job.id), { jobName: newJobName });
      }));
    } catch (error) {
      console.error("Error updating multi-day job numbers:", error);
    }
  }, [jobData, allMultipleDayJobs]);

  const handleJobNumberCallback = async (newValue) => {
    if (newValue && parseInt(newValue)) {
      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(true);
      }

      const duplicates = await checkForDuplicateJobNumbers(newValue);

      if (duplicates.length > 0) {
        setDuplicateJobs(duplicates);
        setPendingJobNumber(parseInt(newValue));
        setShowDuplicateDialog(true);
        setJobNumber(jobData.jobNumber);
        return;
      } else {
        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      }
    }

    if (jobData.client && jobData.jobTypeFull) {
      updateJobName(newValue, jobData.client, jobData.multipleDays, jobData.jobTypeFull);

      if (allMultipleDayJobs.length > 1) {
        await updateMultiDayJobNumbers(newValue);
      }
    }
  };

  const checkForDuplicateJobNumbers = useCallback(async (newJobNumber) => {
    if (!newJobNumber) return [];

    try {
      setIsValidating(true);

      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(true);
      }
      
      const duplicateQuery = query(
        collection(db, "JobData"),
        where("jobNumber", "==", parseInt(newJobNumber))
      );
      
      const querySnapshot = await getDocs(duplicateQuery);
      const duplicates = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (doc.id !== jobData.id) {
          duplicates.push({
            id: doc.id,
            bookedStart: data.bookedStart,
            client: data.client,
            jobTypeFull: data.jobTypeFull,
            multipleDays: data.multipleDays
          });
        }
      });
      
      return duplicates;
    } catch (error) {
      console.error("Error checking for duplicate job numbers:", error);

      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(false);
      }

      return [];
    } finally {
      setIsValidating(false);
    }
  }, [jobData.id, setHasUnsavedChanges]);

  const handleDialogCancel = () => {
    setShowDuplicateDialog(false);
    setDuplicateJobs([]);
    setPendingJobNumber(null);
    setJobNumber(null);

    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  const handleDialogConfirm = async () => {
    if (pendingJobNumber) {
      try {
        await updateDoc(doc(db, "JobData", jobData.id), { jobNumber: parseInt(pendingJobNumber) });
        setJobNumber(pendingJobNumber);

        if (jobData.client && jobData.jobTypeFull) {
          updateJobName(pendingJobNumber, jobData.client, jobData.multipleDays, jobData.jobTypeFull);

          if (allMultipleDayJobs.length > 1) {
            await updateMultiDayJobNumbers(pendingJobNumber);
          }
        }

        setShowDuplicateDialog(false);
        setDuplicateJobs([]);
        setPendingJobNumber(null);

        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error("Error confirming job number:", error);
      }
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'No date';
    
    try {
      let date;
      if (dateValue.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        date = new Date(dateValue);
      }
      
      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getLatestJobNumber = async (jobYear) => {
    try {
      const jobDataQuery = await getDocs(
        query(
          collection(db, "JobData"),
          orderBy("jobNumber", "desc"),
          where("jobNumber", ">=", 0),
          where("year", "==", jobYear),
          limit(20)
        )
      );

      const year = jobYear.toString().substr(-2);
      let newNum = "000";

      if (jobDataQuery.docs.length > 0) {
        let theDoc;
        for (let i = 0; i < jobDataQuery.docs.length; i++) {
          if (jobDataQuery.docs[i].data().client === "Morrissey Media") {
            continue;
          } else if (jobDataQuery.docs[i].data().jobNumber) {
            if (
              jobDataQuery.docs[i].data().jobNumber.toString().substr(0, 2) ===
              year
            ) {
              theDoc = jobDataQuery.docs[i];
              break;
            }
          }
        }
        if (theDoc) {
          newNum = parseInt(theDoc.data().jobNumber.toString().substr(2)) + 1;
        }
      }

      function padInt(value, padding) {
        let zeroes = new Array(padding + 1).join("0");
        return (zeroes + value).slice(-padding);
      }

      let newFullNum;
      if (newNum > 999) {
        newFullNum = parseInt(`${year}${padInt(newNum, 4)}`);
      } else {
        newFullNum = parseInt(`${year}${padInt(newNum, 3)}`);
      }

      return { jobNumber: newFullNum };
    } catch (error) {
      console.error("Error getting latest job number:", error);
      throw error;
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    tap: { scale: 0.98 },
  };

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 2,
        position: "relative",
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <motion.div
              variants={itemVariants}
              whileTap="tap"
              style={{ flex: 1 }}
            >
              <UniversalTextField
                collectionName="JobData"
                docId={jobData.id}
                fieldName="jobNumber"
                value={jobNumber || ""}
                label="Job Number"
                error={jobNumber ? false : true}
                type="number"
                onCallback={handleJobNumberCallback}
                setHasUnsavedChanges={setHasUnsavedChanges}
                adminOnly={true}
                sx={{
                  marginTop: 0,
                  "& .MuiFormControl-root": {
                    marginTop: 0,
                  },
                  "& .MuiInputBase-root": {
                    height: "48px",
                  },
                }}
              />
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                disabled={isLoading || !isAdmin || assignJobNumberDisabled}
                onClick={async () => {
                  if (!isAdmin) return;

                  try {
                    setIsLoading(true);
                    setAssignJobNumberText(
                      <CircularProgress size={24} color="inherit" />
                    );

                    const jobYear = new Date(
                      jobData.bookedStart || new Date()
                    ).getFullYear();
                    const result = await getLatestJobNumber(jobYear);

                    const newJobNumber = parseInt(result.jobNumber);
                    setJobNumber(newJobNumber);

                    const isMultiDay = jobData.isMultiDay || jobData.parentJobId || (jobData.multiDayChildren && jobData.multiDayChildren.length > 0);

                    if (isMultiDay) {
                      const parentId = jobData.parentJobId || jobData.id;
                      const parentJobRef = doc(db, "JobData", parentId);
                      const parentJobSnap = await getDoc(parentJobRef);

                      if (parentJobSnap.exists()) {
                        const parentJobData = parentJobSnap.data();
                        const childIds = parentJobData.multiDayChildren || [];

                        await updateDoc(parentJobRef, { jobNumber: newJobNumber });

                        if (parentJobData.client && parentJobData.jobTypeFull) {
                          const jtsRaw = parentJobData.jobTypeFull?.match(/^.*\((-.*)\)/);
                          const jts = jtsRaw ? jtsRaw[1] : "";
                          const parentJobName = `${newJobNumber}-${parentJobData.client}${
                            parentJobData.multipleDays ? `-${parentJobData.multipleDays}` : ""
                          }${jts}`;
                          await updateDoc(parentJobRef, { jobName: parentJobName });
                        }

                        await Promise.all(childIds.map(async (childId) => {
                          const childRef = doc(db, "JobData", childId);
                          const childSnap = await getDoc(childRef);

                          if (childSnap.exists()) {
                            const childData = childSnap.data();
                            await updateDoc(childRef, { jobNumber: newJobNumber });

                            if (childData.client && childData.jobTypeFull) {
                              const jtsRaw = childData.jobTypeFull?.match(/^.*\((-.*)\)/);
                              const jts = jtsRaw ? jtsRaw[1] : "";
                              const childJobName = `${newJobNumber}-${childData.client}${
                                childData.multipleDays ? `-${childData.multipleDays}` : ""
                              }${jts}`;
                              await updateDoc(childRef, { jobName: childJobName });
                            }
                          }
                        }));
                      }
                    } else {
                      await updateDoc(doc(db, "JobData", jobData.id), { jobNumber: newJobNumber });

                      if (jobData.client && jobData.jobTypeFull) {
                        updateJobName(newJobNumber, jobData.client, jobData.multipleDays, jobData.jobTypeFull);
                      }
                    }
                  } catch (error) {
                    console.error("Error assigning job number:", error);
                  } finally {
                    setIsLoading(false);
                    setAssignJobNumberText("Assign Job Number");
                  }
                }}
                sx={{
                  height: "48px",
                  minWidth: "160px",
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(100,100,255,0.8)"
                      : "rgba(100,100,255,0.9)",
                  padding: "8px 16px",
                  color: "white",
                  fontWeight: 500,
                  textTransform: "none",
                  fontSize: isMobile ? "0.85rem" : "0.95rem",
                  borderRadius: "8px",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 8px rgba(0,0,0,0.3)"
                      : "0 2px 8px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(100,100,255,0.9)"
                        : "rgba(100,100,255,1)",
                    boxShadow:
                      theme === "dark"
                        ? "0 4px 12px rgba(0,0,0,0.4)"
                        : "0 4px 12px rgba(0,0,0,0.2)",
                  },
                  "&:disabled": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(80,80,80,0.2)"
                        : "rgba(0,0,0,0.12)",
                    color:
                      theme === "dark"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.26)",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  assignJobNumberText
                )}
              </Button>
            </motion.div>
          </Box>
        </motion.div>
      </Box>

      {/* Duplicate Job Number Dialog */}
      <Dialog
        open={showDuplicateDialog}
        onClose={isValidating ? undefined : handleDialogCancel}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: theme === 'dark' ? 'rgba(35,35,35,0.95)' : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            color: theme === 'dark' ? '#ffffff' : '#000000',
            borderRadius: 2,
            boxShadow: theme === 'dark' 
              ? '0 8px 32px rgba(0,0,0,0.4)' 
              : '0 8px 32px rgba(0,0,0,0.15)',
            border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: theme === 'dark' ? '#90caf9' : '#1976d2',
            borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            paddingBottom: 2,
          }}
        >
          Duplicate Job Number Found
        </DialogTitle>
        
        <DialogContent sx={{ paddingTop: 3 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              marginBottom: 3,
              color: theme === 'dark' ? '#ffffff' : '#000000',
            }}
          >
            {duplicateJobs.length === 1 
              ? `There is another job with the same job number (${pendingJobNumber}).`
              : `There are ${duplicateJobs.length} other jobs with the same job number (${pendingJobNumber}).`
            }
          </Typography>

          <TableContainer 
            component={Paper} 
            sx={{ 
              backgroundColor: theme === 'dark' ? 'rgba(50,50,50,0.8)' : 'rgba(248,248,248,0.8)',
              boxShadow: 'none',
              border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              borderRadius: 2,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    color: theme === 'dark' ? '#ffffff' : '#000000',
                    backgroundColor: theme === 'dark' ? 'rgba(70,70,70,0.8)' : 'rgba(240,240,240,0.8)',
                    borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    color: theme === 'dark' ? '#ffffff' : '#000000',
                    backgroundColor: theme === 'dark' ? 'rgba(70,70,70,0.8)' : 'rgba(240,240,240,0.8)',
                    borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  }}>
                    Client
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    color: theme === 'dark' ? '#ffffff' : '#000000',
                    backgroundColor: theme === 'dark' ? 'rgba(70,70,70,0.8)' : 'rgba(240,240,240,0.8)',
                    borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  }}>
                    Job Type
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    color: theme === 'dark' ? '#ffffff' : '#000000',
                    backgroundColor: theme === 'dark' ? 'rgba(70,70,70,0.8)' : 'rgba(240,240,240,0.8)',
                    borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  }}>
                    Multiple Days
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {duplicateJobs.map((job, index) => (
                  <TableRow key={job.id || index}>
                    <TableCell sx={{ 
                      color: theme === 'dark' ? '#ffffff' : '#000000',
                      borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    }}>
                      {formatDate(job.bookedStart)}
                    </TableCell>
                    <TableCell sx={{ 
                      color: theme === 'dark' ? '#ffffff' : '#000000',
                      borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    }}>
                      {job.client || 'No client'}
                    </TableCell>
                    <TableCell sx={{ 
                      color: theme === 'dark' ? '#ffffff' : '#000000',
                      borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    }}>
                      {job.jobTypeFull || 'No job type'}
                    </TableCell>
                    <TableCell sx={{ 
                      color: theme === 'dark' ? '#ffffff' : '#000000',
                      borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                    }}>
                      {job.multipleDays || 'No'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ padding: 3, gap: 2 }}>
          <Button
            onClick={handleDialogCancel}
            variant="outlined"
            disabled={isValidating}
            sx={{
              color: theme === 'dark' ? '#ffffff' : '#000000',
              borderColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
              '&:hover': {
                borderColor: theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              },
              '&:disabled': {
                borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                color: theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDialogConfirm}
            variant="contained"
            disabled={isValidating}
            sx={{
              backgroundColor: theme === 'dark' ? '#90caf9' : '#1976d2',
              color: theme === 'dark' ? '#000000' : '#ffffff',
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: theme === 'dark' ? '#64b5f6' : '#1565c0',
              },
              '&:disabled': {
                backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                color: theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              }
            }}
          >
            {isValidating ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </FormControl>
  );
};

export default JobNumber;
